import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { RootState } from 'core/store';
import { gaBtnClickEvent, isImageFileUrl } from 'core/util';
import { filterItems } from 'components/searchbar/SearchBar';
import { fetchAllDocuments } from 'features/shared/documents/DocumentsActions';
import {
  setCurrentDocumentType,
  setSelectedDocument,
  showDocumentsAddModal,
  hideDocumentsAddModal,
  showDesktopDocumentEditModal,
  setDocumentNavigation,
  resetDocumentsRefreshFlag,
  setPhotosCountFromDocuments,
  ViewMode
} from 'features/shared/documents/DocumentsSlice';
import { Document as CoreDocument, SearchedDocument } from 'core/types';

interface DocumentParams {
  type?: string | undefined;
}

export const useDocumentsList = () => {
  const { type } = useParams<DocumentParams>();
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();

  // Selectors
  const {
    loading,
    documents,
    types,
    addModalVisible,
    editDesktopModalVisible,
    viewMode,
    documentsNeedRefresh
  } = useSelector((state: RootState) => state.documents);

  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );

  // Computed values
  const filteredDocuments = filterItems(
    documents || [],
    ['name'],
    search
  ) as CoreDocument[];

  const nonImageDocuments = filteredDocuments.filter(
    (doc) => !isImageFileUrl(doc.file_url)
  );
  const imageDocuments = filteredDocuments.filter((doc) =>
    isImageFileUrl(doc.file_url)
  );

  const getDocumentTypeLabel = (): string => {
    if (types && types.length && type) {
      const matchingType = types.find(
        (documentType) => documentType.field === type
      );
      if (matchingType) {
        return matchingType.label;
      }
    }
    return 'Manuals';
  };

  // Actions
  const showAddDocumentsModal = (type: string) => {
    gaBtnClickEvent('open_add_documents_modal');
    dispatch(setCurrentDocumentType(type));
    dispatch(showDocumentsAddModal());
  };

  const closeAddDocumentsModal = () => {
    if (!!homeId && !!type) {
      dispatch(fetchAllDocuments(homeId, type));
    }
    dispatch(setCurrentDocumentType(null));
    dispatch(hideDocumentsAddModal());
  };

  const convertDocumentToSearchedDocument = (doc: any): SearchedDocument => {
    return {
      file_url: doc.file_url,
      id: doc.id,
      image_id: doc.image_id || null,
      name: doc.name,
      opt_home_id: homeId || 0,
      optimized_url: doc.optimized_url || null,
      thumbnail_url: doc.thumbnail_url || null,
      type: doc.type,
      type_id: doc.type_id,
      type_label: getDocumentTypeLabel()
    };
  };

  const openDesktopDocument = (document: SearchedDocument | CoreDocument) => {
    const selectedDoc = document as SearchedDocument;
    dispatch(setSelectedDocument(selectedDoc));

    // Use the current documents list (filtered if search is active, otherwise all documents)
    const currentDocumentsList = search ? filteredDocuments : documents || [];

    // Convert documents to SearchedDocument format for navigation
    const searchedDocuments = currentDocumentsList.map(
      convertDocumentToSearchedDocument
    );
    const currentIndex = searchedDocuments.findIndex(
      (doc) => doc.id === selectedDoc.id
    );

    dispatch(
      setDocumentNavigation({
        documents: searchedDocuments,
        currentIndex: currentIndex
      })
    );

    dispatch(showDesktopDocumentEditModal());
  };

  const openMobileDocument = (document: SearchedDocument) => {
    dispatch(setSelectedDocument(document));

    // Set navigation context for mobile as well
    const currentDocumentsList = search ? filteredDocuments : documents || [];
    const searchedDocuments = currentDocumentsList.map(
      convertDocumentToSearchedDocument
    );
    const currentIndex = searchedDocuments.findIndex(
      (doc) => doc.id === document.id
    );

    dispatch(
      setDocumentNavigation({
        documents: searchedDocuments,
        currentIndex: currentIndex
      })
    );

    dispatch(showDesktopDocumentEditModal());
  };

  // Keep photosCount in sync when on Photos page (same count as "X Items" display)
  useEffect(() => {
    if (type === 'photos' && documents) {
      dispatch(setPhotosCountFromDocuments({ items: documents }));
    }
  }, [type, documents, dispatch]);

  // Effects
  useEffect(() => {
    const shouldFetchDocuments =
      !!homeId &&
      !!type &&
      // Fetch on initial load or when type/homeId changes if no documents exist in redux
      (!documents ||
        // Refresh list of documents after modal crud operations
        (editDesktopModalVisible === false && documentsNeedRefresh));

    if (shouldFetchDocuments) {
      dispatch(fetchAllDocuments(homeId, type));

      // Reset refresh flag if this was a modal-triggered refresh
      if (documentsNeedRefresh) {
        dispatch(resetDocumentsRefreshFlag());
      }
    }
  }, [
    homeId,
    type,
    documents,
    editDesktopModalVisible,
    documentsNeedRefresh,
    dispatch
  ]);

  return {
    // State
    type,
    search,
    setSearch,
    loading,
    documents,
    filteredDocuments,
    nonImageDocuments,
    imageDocuments,
    addModalVisible,
    editDesktopModalVisible,
    viewMode: viewMode as ViewMode,
    platformType,
    isDesktopWidth,

    // Computed
    getDocumentTypeLabel,

    // Actions
    showAddDocumentsModal,
    closeAddDocumentsModal,
    openMobileDocument,
    openDesktopDocument,

    // Dispatch for direct use
    dispatch
  };
};
